import React, { useState } from 'react';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { fg } from '@atlaskit/platform-feature-flags';

import { useExtensionListRelay as useExtensionList } from '../../web-client/hooks/useExtensionList';
import { GQLExtensionContextsFilterType } from '../../web-client/graphql/types';
import { Loader } from '../../web-runtime';

import { ForgeUIRenderer } from './main';
import { ExtensionNotInstalled } from './ExtensionNotInstalled';
import type {
	ForgeUIRendererProps,
	CloudOrWorkspaceId,
	ConfluenceCustomUIProps,
	EmbeddedUIKitModalContext,
} from './types';
import type { GQLExtension } from '../../web-client/graphql/types';
import { EmbeddedModalContext } from './EmbeddedModalContext';
import { createMessageForwarder } from '../../custom-ui/bridge/utils/embedded-macros/createMessageForwarder';
import { isCustomUI } from '../..';
import { ReservedHeightWrapper } from './ReservedHeightWrapper';
import { calculateViewportSizeHeight, type ViewportSizeType } from './calculateViewportSizeHeight';

type PostRobotSend = typeof import('@atlassian/bridge-core').postRobotSend;

let cachedPostRobotSend: PostRobotSend | null | undefined;

const getPostRobotSend = (): PostRobotSend | null => {
	if (cachedPostRobotSend !== undefined) cachedPostRobotSend;

	if (typeof window === 'undefined') return null;

	try {
		cachedPostRobotSend = require('@atlassian/bridge-core').postRobotSend;
	} catch (_) {
		cachedPostRobotSend = null;
	}
	return cachedPostRobotSend;
};

type EmbeddedMacroRendererProps = Omit<ForgeUIRendererProps, 'extension'> &
	CloudOrWorkspaceId &
	ConfluenceCustomUIProps & {
		createGetContextToken?: (extension: GQLExtension) => () => Promise<string>;
		extensionId: string;
		isInCustomUI?: boolean;
		isInline?: boolean;
	};

type MacroExtensionContainerProps = {
	children: React.ReactNode;
	isInline?: boolean;
};

// Container for supporting inline macros
const MacroExtensionContainer = ({ isInline, children }: MacroExtensionContainerProps) => {
	const inlineEnabled = fg('platform_forge_ui_embedded_inline_macro') && isInline;
	return (
		<div
			data-testid="ForgeExtensionContainer"
			style={{
				display: inlineEnabled ? 'inline-block' : 'block',
				verticalAlign: inlineEnabled ? 'middle' : '',
			}}
		>
			{children}
		</div>
	);
};

const getLoadingComponent = (loadingComponent?: React.ReactNode) => {
	if (loadingComponent) {
		return <>{loadingComponent}</>;
	}
	return <Loader />;
};

const EmbeddedMacroRendererInner = (
	props: EmbeddedMacroRendererProps,
): React.JSX.Element | null => {
	const {
		extensionId,
		contextIds,
		loadingComponent,
		createGetContextToken,
		extensionData,
		embeddedParentLocalId,
		localId,
		extensionPayload,
		isInCustomUI,
		entryPoint,
		customBridge,
		...rest
	} = props;
	const [embeddedParentModalKey, setEmbeddedParentModalKey] = useState<string | undefined>(
		undefined,
	);

	const { extensions, loading } = useExtensionList({
		contextIds,
		filter: [
			{
				type: GQLExtensionContextsFilterType.ExtensionType,
				value: ['xen:macro'],
			},
		],
	});

	const extension = extensions?.find((ext) => ext.id === extensionId);
	const queryCompleted = extensions !== undefined;
	const notInstalled =
		queryCompleted && !extension && fg('platform_validate_embedded_macro_installation');

	if (loading || !queryCompleted) {
		return getLoadingComponent(loadingComponent);
	}

	if (notInstalled) {
		return <ExtensionNotInstalled extensionId={extensionId} />;
	}

	if (!extension) {
		return null;
	}

	type ForgeUIRendererPropsWithExtension = React.ComponentProps<typeof ForgeUIRenderer>;

	let getContextToken = props.getContextToken;

	// Create getContextToken function with fresh extension object
	if (createGetContextToken) {
		getContextToken = createGetContextToken(extension);
	}
	const isEmbeddedUIKitInCustomUI = isInCustomUI && !isCustomUI(extension, entryPoint);

	const forgeRendererProps: ForgeUIRendererPropsWithExtension = {
		...rest,
		customBridge,
		contextIds,
		extension,
		loadingComponent,
		getContextToken,
		extensionData,
		embeddedParentLocalId,
		localId,
		extensionPayload,
		entryPoint,
		isEmbeddedUIKitInCustomUI,
		isEmbeddedMacro: true,
		height:
			extension.properties?.viewportSize && fg('platform_forge_ui_embedded_inline_macro')
				? calculateViewportSizeHeight(extension.properties.viewportSize as ViewportSizeType)
				: undefined,
	};

	if (isEmbeddedUIKitInCustomUI && fg('platform_forge_ui_uikit_modal_in_custom_ui_parent')) {
		const postRobotSend = getPostRobotSend(); // This prop is used to determine if the embedded macro is rendered within a Custom UI macro. Since the EmbeddedMacroRenderer is only used for rendering macros within Custom UI, we can set this to true.
		const uiKitModalForwarder =
			fg('forge-ui-bridge-core-reset') && customBridge?.['setEmbeddedMacroModalForgeDoc'];
		const forwarder = uiKitModalForwarder
			? ({ data }: { data: { embeddedUIKitModalContext: EmbeddedUIKitModalContext } }) =>
					uiKitModalForwarder({} as any, data)
			: createMessageForwarder(
					'setEmbeddedMacroModalForgeDoc',
					{
						embeddedMacroExtensionDetails: undefined,
						embeddedMacroLocalId: localId,
						extension,
						extensionData,
						extensionPayload,
						isEmbeddedMacroModal: true,
						sourceAppId: extension?.appId ?? '',
						sourceEnvironmentId: extension?.environmentKey,
						sourceInstallationId: extension?.installationId,
					},
					postRobotSend,
					embeddedParentLocalId,
					{
						appId: extension?.appId ?? '',
						id: extension?.id ?? '',
						installationId: extension?.installationId,
					},
				);
		return (
			<ReservedHeightWrapper extension={extension}>
				<EmbeddedModalContext.Provider
					value={{
						embeddedParentModalKey,
						setEmbeddedParentModalKey,
						forwarder,
					}}
				>
					<ForgeUIRenderer {...forgeRendererProps} />
				</EmbeddedModalContext.Provider>
			</ReservedHeightWrapper>
		);
	}
	return (
		<ReservedHeightWrapper extension={extension}>
			<ForgeUIRenderer {...forgeRendererProps} />
		</ReservedHeightWrapper>
	);
};

// This wrapper is required to use the Relay environment provider for the useExtensionListRelay hook.
// Once Confluence has fully migrated to Relay, we can remove this wrapper and defer to their Relay environment
export const EmbeddedMacroRenderer = (props: EmbeddedMacroRendererProps): React.JSX.Element => {
	return (
		<AtlassianRelayEnvironmentProvider>
			<MacroExtensionContainer isInline={props.isInline}>
				<EmbeddedMacroRendererInner {...props} />
			</MacroExtensionContainer>
		</AtlassianRelayEnvironmentProvider>
	);
};
